import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { ItemInfoColumnsProps, useItemInfoColumns } from './useItemInfoColumns';
import { Box, IconButton } from '@mui/material';
import { TrashIcon } from '@sanity/icons';
import { useUnitColumns } from './useUnitColumns';
import { usePurchasePriceColumns } from './usePurchasePriceColumns';
import { usePriceSuggestionColumns } from './usePriceSuggestionColumns';
import { useSalesCoverageColumns } from './useSalesCoverageColumns';
import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';

export type GetCampaignItemsColumnsFn<TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem> =
  (props: {
    deleteItem: (itemId: number) => void;
    incVat: boolean;
  }) => GridColDef<TMgSupplierCampaignItem>[];

export const addItemsBaseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
  sortable: false,
};

export const useAddItemsBaseColumns = <T extends BaseMgSupplierCampaignItem>(
  props: ItemInfoColumnsProps
) => {
  const { getItemIdsColumn, getItemNameColumn, getSupplierColumn } = useItemInfoColumns(props);
  const { getPrimaryPackageUnitColumn, getCampaignUnitColumn } = useUnitColumns();
  const { getCampaignPurchasePriceColumn } = usePurchasePriceColumns();
  const { getSalesCoverageColumn } = useSalesCoverageColumns();
  const { getFinfoRecPriceColumn, getFinfoBasePriceColumn } = usePriceSuggestionColumns();

  return useMemo(
    () => ({
      getItemIdsColumn,
      getItemNameColumn,
      getSupplierColumn,
      getPrimaryPackageUnitColumn,
      getCampaignUnitColumn,
      getCampaignPurchasePriceColumn,
      getSalesCoverageColumn,
      getFinfoBasePriceColumn,
      getFinfoRecPriceColumn,
      getActionsColumn: (deleteItem: (itemId: number) => void): GridColDef<T> =>
        ({
          ...addItemsBaseColumnProps,
          field: 'actions',
          width: 75,
          sortable: false,
          hideable: false,
          headerAlign: 'center',
          align: 'center',
          disableColumnMenu: true,
          renderCell: ({ row }) => (
            <Box>
              <IconButton onClick={() => deleteItem(row.mgSupplierItemId)}>
                <TrashIcon fontSize="25px" />
              </IconButton>
            </Box>
          ),
        } as GridColDef),
    }),
    [
      getItemIdsColumn,
      getItemNameColumn,
      getSupplierColumn,
      getPrimaryPackageUnitColumn,
      getCampaignUnitColumn,
      getCampaignPurchasePriceColumn,
      getSalesCoverageColumn,
      getFinfoBasePriceColumn,
      getFinfoRecPriceColumn,
    ]
  );
};
